import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {
    Brush, CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';
import {downloadSensorReportCsv, downloadSensorReportPdf} from '../../store/actions/sensorReportsActions';
import './CommunitySensorHistory.scss';
import {
    AGGREGATIONS,
    buildReportBody,
    defaultAggregation,
    fetchSensorReport,
    isLastWindow,
    parameterOptions,
    shiftWindow,
    summarize,
    todayLocal,
    toChartSeries,
    windowEndingAt
} from './sensorHistoryApi';

const PERIODS = [1, 7, 30, 90];
const LINE_COLORS = {min: '#2f6fdb', avg: '#2c9a2c', max: '#d9534f', value: '#2c9a2c', sum: '#ff7300'};
const PARAMETER_LABELS = {
    temperature: 'Temperature', humidity: 'Humidity', soil_moisture: 'Soil Moisture',
    soil_conductivity: 'Soil Conductivity', ph: 'pH', ec: 'EC', wind_speed: 'Wind Speed', wet_leaf: 'Wet Leaf',
    rain: 'Rain', battery: 'Battery'
};
const STATISTIC_LABELS = {min: 'Min', avg: 'Average', max: 'Max', sum: 'Total', value: 'Value', minutes: 'Minutes'};

const formatNumber = value => (value === null || value === undefined ? '–' : String(Math.round(value * 100) / 100));
const formatTime = ms => new Date(ms).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
});

const CommunitySensorHistory = () => {
    const t = useSelector(state => state.language.translator);
    const dispatch = useDispatch();
    const sensorId = Number(useParams().sensorId);

    const [window, setWindow] = useState(() => windowEndingAt(todayLocal(), 7));
    const [aggregation, setAggregation] = useState(() => defaultAggregation(7));
    const [tab, setTab] = useState('reports');
    const [parameter, setParameter] = useState(null);
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetchSensorReport(buildReportBody({sensorId, dateFrom: window.dateFrom, dateTo: window.dateTo,
            aggregationPeriod: aggregation}))
            .then(result => {
                if (cancelled) return;
                setReport(result);
                setLoading(false);
            })
            .catch(e => {
                if (cancelled) return;
                setError(`Could not load the report (${e.message}).`);
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [sensorId, window.dateFrom, window.dateTo, aggregation]);

    const options = useMemo(() => parameterOptions(report), [report]);
    const activeParameter = parameter && options.some(o => o.parameterKey === parameter)
        ? parameter : (options[0] && options[0].parameterKey);
    const series = useMemo(() => toChartSeries(report, activeParameter), [report, activeParameter]);
    const activeOption = options.find(o => o.parameterKey === activeParameter);
    const statistics = activeOption ? activeOption.columns.map(c => c.statisticKey || 'value') : [];
    const lastWindow = isLastWindow(window, todayLocal());

    const choosePeriod = days => {
        setWindow(windowEndingAt(window.dateTo, days));
        setAggregation(defaultAggregation(days));
    };
    const chooseEndDate = value => {
        if (value) setWindow(windowEndingAt(value > todayLocal() ? todayLocal() : value, window.days));
    };
    const filters = {sensorIds: [sensorId], dateFrom: window.dateFrom, dateTo: window.dateTo,
        aggregationPeriod: aggregation, withBattery: false};
    const label = key => t.gettext(PARAMETER_LABELS[key] || key);
    const unit = activeOption && activeOption.unit ? ` ${activeOption.unit}` : '';

    return (
        <div className='CommunitySensorHistory'>
            <div className='sensorHistoryPanel'>
                <div className='sensorHistoryHeader'>
                    <div>
                        <h2>{report && report.sensorName ? report.sensorName : t.gettext('Sensor')}
                            <span className='sensorHistoryMuted'> #{sensorId}</span></h2>
                        <div className='sensorHistoryMuted'>{window.dateFrom} – {window.dateTo}</div>
                    </div>
                    <div className='sensorHistoryDownloads'>
                        <button type='button' className='sensorHistoryButton sensorHistoryButton--secondary'
                                onClick={() => dispatch(downloadSensorReportCsv(filters))}>{t.gettext('Download CSV')}</button>
                        <button type='button' className='sensorHistoryButton sensorHistoryButton--secondary'
                                onClick={() => dispatch(downloadSensorReportPdf(filters))}>{t.gettext('Download PDF')}</button>
                    </div>
                </div>

                <div className='sensorHistoryControls'>
                    <div className='sensorHistoryPeriods' role='group' aria-label={t.gettext('Period')}>
                        {PERIODS.map(days => (
                            <button key={days} type='button' aria-pressed={window.days === days}
                                    onClick={() => choosePeriod(days)}>
                                {days === 1 ? t.gettext('1 day') : `${days} ${t.gettext('days')}`}
                            </button>
                        ))}
                    </div>
                    <div className='sensorHistoryNavigation'>
                        <button type='button' className='sensorHistoryButton'
                                onClick={() => setWindow(shiftWindow(window, -1))}>{t.gettext('Previous period')}</button>
                        <input type='date' value={window.dateTo} max={todayLocal()}
                               aria-label={t.gettext('Period ends on')} onChange={e => chooseEndDate(e.target.value)}/>
                        <button type='button' className='sensorHistoryButton' disabled={lastWindow}
                                onClick={() => setWindow(shiftWindow(window, 1))}>{t.gettext('Next period')}</button>
                    </div>
                    <label className='sensorHistoryAggregation'>
                        {t.gettext('Detail')}
                        <select value={aggregation} onChange={e => setAggregation(e.target.value)}>
                            {AGGREGATIONS.map(value => (
                                <option key={value} value={value}>
                                    {{RAW: t.gettext('All measurements'), MIN_30: t.gettext('30 minutes'),
                                        HOUR: t.gettext('Hour'), DAY: t.gettext('Day')}[value]}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>

                <div className='sensorHistoryTabs' role='tablist'>
                    <button type='button' role='tab' aria-selected={tab === 'reports'}
                            onClick={() => setTab('reports')}>{t.gettext('Reports')}</button>
                    <button type='button' role='tab' aria-selected={tab === 'charts'}
                            onClick={() => setTab('charts')}>{t.gettext('Charts')}</button>
                </div>

                {error && <div className='sensorHistoryError' role='alert'>{t.gettext(error)}</div>}
                {loading && !error && <div className='sensorHistoryMuted'>{t.gettext('Loading…')}</div>}

                {report && options.length > 0 && (
                    <div className='sensorHistoryParameters' role='group' aria-label={t.gettext('Measurement')}>
                        {options.map(option => (
                            <button key={option.parameterKey} type='button'
                                    aria-pressed={option.parameterKey === activeParameter}
                                    onClick={() => setParameter(option.parameterKey)}>
                                {label(option.parameterKey)}
                            </button>
                        ))}
                    </div>
                )}

                {report && tab === 'reports' && (
                    <>
                        <div className='sensorHistoryCards'>
                            {['min', 'avg', 'max'].map(kind => {
                                const summary = summarize(series);
                                return (
                                    <div key={kind} className='sensorHistoryCard'>
                                        <div className='sensorHistoryMuted'>
                                            {{min: t.gettext('Lowest'), avg: t.gettext('Average'), max: t.gettext('Highest')}[kind]}
                                        </div>
                                        <strong>{formatNumber(summary[kind])}{unit}</strong>
                                    </div>
                                );
                            })}
                            <div className='sensorHistoryCard'>
                                <div className='sensorHistoryMuted'>{t.gettext('Measurements')}</div>
                                <strong>{summarize(series).count}</strong>
                            </div>
                        </div>
                        <div className='sensorHistoryTableWrap'>
                            <table className='sensorHistoryTable'>
                                <thead>
                                <tr>
                                    <th>{t.gettext('Date/Time')}</th>
                                    {report.columns.map(column => (
                                        <th key={column.key}>
                                            {label(column.parameterKey)} {t.gettext(STATISTIC_LABELS[column.statisticKey] || '')} {column.unit}
                                        </th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {[...report.rows].reverse().map(row => (
                                    <tr key={row.timestampFrom}>
                                        <td>{row.timestampFrom.split('T').join(' ')}</td>
                                        {report.columns.map(column => (
                                            <td key={column.key}>
                                                {row.values && row.values[column.key] != null ? row.values[column.key] : '–'}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                {report.rows.length === 0 && (
                                    <tr><td colSpan={report.columns.length + 1} className='sensorHistoryMuted'>
                                        {t.gettext('No measurements in this period.')}
                                    </td></tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {report && tab === 'charts' && (
                    <div className='sensorHistoryChart'>
                        {series.length === 0
                            ? <div className='sensorHistoryMuted'>{t.gettext('No measurements in this period.')}</div>
                            : (
                                <ResponsiveContainer width='100%' height={380}>
                                    <LineChart data={series} margin={{top: 10, right: 20, bottom: 10, left: 0}}>
                                        <CartesianGrid strokeDasharray='3 3'/>
                                        <XAxis dataKey='time' type='number' scale='time' domain={['dataMin', 'dataMax']}
                                               tickFormatter={formatTime}/>
                                        <YAxis unit={unit} domain={['auto', 'auto']}/>
                                        <Tooltip labelFormatter={formatTime}
                                                 formatter={(value, name) => [`${formatNumber(value)}${unit}`, t.gettext(STATISTIC_LABELS[name] || name)]}/>
                                        <Legend formatter={name => t.gettext(STATISTIC_LABELS[name] || name)}/>
                                        {activeParameter === 'temperature' && <ReferenceLine y={0} stroke='#2f6fdb' strokeDasharray='4 4'/>}
                                        {statistics.map(statistic => (
                                            <Line key={statistic} type='monotone' dataKey={statistic}
                                                  stroke={LINE_COLORS[statistic] || '#8884d8'} dot={false} connectNulls
                                                  isAnimationActive={false}/>
                                        ))}
                                        <Brush dataKey='time' height={28} tickFormatter={formatTime}/>
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        <div className='sensorHistoryMuted'>
                            {t.gettext('Drag the handles under the chart to zoom, or use the previous and next period buttons to move through the history.')}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommunitySensorHistory;
